/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  initPageable,
  PaginationResponse,
  NestedErrorMessage,
} from "@advapacs/common";
import { defaultValue, IUser } from "../../../../types/user";
const initialState = {
  loading: false,
  errorMessage: null as NestedErrorMessage | null,
  entities: [] as Array<IUser>,
  entity: defaultValue,
  isUpdated: false,
  updatedPassword: false,
  updatePasswordErrorMessage: null as NestedErrorMessage | null,
  pageable: initPageable,
  showFilter: false,
  filter: {
    organisations: [] as Array<string>,
    status: true,
    searchKeyword: undefined as string | undefined,
  },
};

const userSlice = createSlice({
  name: "userSlice",
  initialState,
  reducers: {
    startSearchUsers(state) {
      state.loading = true;
      state.isUpdated = false;
    },
    failSearchUsers(state, action: PayloadAction<NestedErrorMessage | null>) {
      state.loading = false;
      state.entities = [];
      state.errorMessage = action.payload;
    },
    searchUsersSuccess(
      state,
      action: PayloadAction<PaginationResponse<IUser>>
    ) {
      state.loading = false;
      state.entities = action.payload.content;
      state.pageable = action.payload.pageable;
    },
    getUserDetailStart(state) {
      state.loading = false;
      state.isUpdated = false;
      state.errorMessage = null;
      state.updatedPassword = false;
    },
    getUserDetailSuccess(state, action: PayloadAction<IUser>) {
      state.loading = false;
      state.entity = { ...action.payload, totp: state.entity.totp };
      state.isUpdated = false;
    },
    getUserDetailFail(state, action: PayloadAction<NestedErrorMessage | null>) {
      state.loading = false;
      state.errorMessage = action.payload;
    },
    getUserTwoFactorStart(state) {
      state.isUpdated = false;
      state.errorMessage = null;
    },
    getUserTwoFactorSuccess(state, action: PayloadAction<boolean>) {
      state.loading = false;
      state.entity = {
        ...state.entity,
        totp: action.payload,
      };
    },
    getUserTwoFactorFail(
      state,
      action: PayloadAction<NestedErrorMessage | null>
    ) {
      state.loading = false;
      state.errorMessage = action.payload;
    },
    clearData(state) {
      state.entities = [];
      state.entity = defaultValue;
      state.loading = false;
      state.isUpdated = false;
      state.updatedPassword = false;
      state.errorMessage = null;
    },
    clearErrorMessage(state) {
      state.errorMessage = null;
    },
    updateUserStart(state) {
      state.loading = true;
      state.isUpdated = false;
    },
    updateUserSuccess(state) {
      state.isUpdated = true;
      state.loading = false;
      state.errorMessage = null;
      state.entity.password = "";
      state.entity.confirmPassword = "";
    },
    updateUserFail(state, action: PayloadAction<NestedErrorMessage>) {
      state.isUpdated = false;
      state.errorMessage = action.payload;
      state.loading = false;
      state.entity.password = "";
      state.entity.confirmPassword = "";
    },
    createUserStart(state) {
      state.loading = true;
      state.isUpdated = false;
      state.errorMessage = null;
    },
    createUserSuccess(state) {
      state.isUpdated = true;
      state.loading = false;
      state.errorMessage = null;
      state.entity.password = "";
      state.entity.confirmPassword = "";
    },
    createUserFail(state, action: PayloadAction<NestedErrorMessage>) {
      state.isUpdated = false;
      state.errorMessage = action.payload;
      state.loading = false;
      state.entity.password = "";
      state.entity.confirmPassword = "";
    },
    resetIsUpdated(state) {
      state.isUpdated = false;
    },
    setShowFilter(state) {
      state.showFilter = !state.showFilter;
    },
    setFilterChange(
      state,
      action: PayloadAction<{
        organisations: Array<string>;
        status: boolean;
        searchKeyword: string | undefined;
      }>
    ) {
      state.filter = action.payload;
    },
    resetFilter(state) {
      state.filter = initialState.filter;
    },
    updatePasswordStart(state) {
      state.updatedPassword = false;
      state.isUpdated = false;
      state.loading = false;
      state.updatePasswordErrorMessage = null;
    },
    updatePasswordSuccess(state) {
      state.updatedPassword = true;
      state.loading = false;
      state.updatePasswordErrorMessage = null;
    },
    updatePasswordFail(state, action: PayloadAction<NestedErrorMessage>) {
      state.updatedPassword = false;
      state.isUpdated = false;
      state.loading = false;
      state.updatePasswordErrorMessage = action.payload;
    },
    updatePasswordClear(state) {
      state.updatedPassword = false;
      state.loading = false;
      state.updatePasswordErrorMessage = null;
    },
    setErrorMessage(state, action) {
      state.errorMessage = action.payload;
    },
  },
});

export const UserAction = userSlice.actions;
export default userSlice.reducer;
